import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import {
  JournalDraftEntryLineFragment,
  JournalEntryLineFragment,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

interface JournalLineFooterProps {
  locale: string
  journalEntryLines: (JournalEntryLineFragment | JournalDraftEntryLineFragment)[]
  currency: string
  disabled?: boolean
}

export const JournalLineFooter = ({
  locale,
  journalEntryLines,
  currency,
  disabled,
}: JournalLineFooterProps) => {
  const itemsTotalAmount: { credit: string; debit: string } = useUnsafeMemo(() => {
    const { sumDebit, sumCredit } = journalEntryLines.reduce(
      (sums, el) => {
        sums.sumDebit += el.debitAmountCents || 0
        sums.sumCredit += el.creditAmountCents || 0
        return sums
      },
      { sumDebit: 0, sumCredit: 0 }
    )

    const formatAmount = (amountCents: number) =>
      formattedAmount({ amountCents, currency, locale }) ||
      formattedAmount({ amountCents: 0, currency, locale })

    return {
      debit: formatAmount(sumDebit),
      credit: formatAmount(sumCredit),
    }
  }, [journalEntryLines, locale, currency])

  const creditAndDebitNotMatching = useUnsafeMemo(() => {
    return itemsTotalAmount['debit'] !== itemsTotalAmount['credit']
  }, [itemsTotalAmount])

  return (
    <Box
      width="full"
      flexDirection="row"
      alignItems="center"
      justifyContent="spaceBetween"
      style={{ minHeight: '40px', marginLeft: '-6px' }}
    >
      <Box flexDirection="row" gap="s48">
        {disabled ? (
          <Text.Normal size="200" weight="semibold" color="grey1000">
            {i18n.t('finance.journal_entry.journal_line.footer.balance')}
          </Text.Normal>
        ) : (
          <Tag
            showCircle
            type={creditAndDebitNotMatching ? 'radical' : 'lime'}
            leftItem="icon"
            icon={creditAndDebitNotMatching ? 'alert-circle' : 'check'}
            iconColor={creditAndDebitNotMatching ? 'radicalRed1200' : 'viridian1200'}
          >
            <Text.Normal
              size="200"
              weight="medium"
              color={creditAndDebitNotMatching ? 'radicalRed1200' : 'viridian1200'}
              whiteSpace="breakSpaces"
            >
              {creditAndDebitNotMatching
                ? i18n.t('finance.journal_entry.journal_line.footer.unbalanced')
                : i18n.t('finance.journal_entry.journal_line.footer.balanced')}
            </Text.Normal>
          </Tag>
        )}
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween" width="s300">
        <Box
          textAlign={disabled ? 'right' : 'left'}
          justifyContent="center"
          paddingLeft={disabled ? 's0' : 's32'}
          paddingRight={disabled ? 's8' : 's0'}
          style={{ width: '150px' }}
        >
          <Text.Normal size="200" weight="semibold" color={'grey1000'} whiteSpace="breakSpaces">
            {itemsTotalAmount['debit']}
          </Text.Normal>
        </Box>
        <Box
          textAlign="left"
          justifyContent="center"
          paddingLeft={disabled ? 's64' : 's8'}
          style={{ width: '150px' }}
        >
          <Text.Normal size="200" weight="semibold" color={'grey1000'} whiteSpace="breakSpaces">
            {itemsTotalAmount['credit']}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}
